import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { Settings as SettingsIcon, Key, Globe, Code, Copy, Check, Loader2 } from 'lucide-react';
import { useState } from 'react';

export default function Settings() {
  const { apiKey, apiUrl } = useAuthStore();
  const [copied, setCopied] = useState<string | null>(null);

  const { data: site, isLoading } = useQuery({
    queryKey: ['site'],
    queryFn: api.getSite
  });

  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: api.getTemplates
  });

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500">Manage your site configuration and API access</p>
      </div>

      {/* Site Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <SettingsIcon className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Site Information</h2>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm text-gray-500">Site Name</dt>
            <dd className="text-sm font-medium text-gray-900">{site?.name}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Business Type</dt>
            <dd className="text-sm font-medium text-gray-900 capitalize">
              {site?.business_type?.replace(/-/g, ' ')}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Domain</dt>
            <dd className="text-sm font-medium text-gray-900">{site?.domain || 'Not configured'}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Site ID</dt>
            <dd className="text-sm font-mono text-gray-900">{site?.id}</dd>
          </div>
        </dl>
      </div>

      {/* Content Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <Globe className="h-5 w-5 text-green-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Content Settings</h2>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm text-gray-500">Language</dt>
            <dd className="text-sm font-medium text-gray-900">{site?.settings?.language}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Content Tone</dt>
            <dd className="text-sm font-medium text-gray-900 capitalize">{site?.settings?.content_tone}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Target Audience</dt>
            <dd className="text-sm font-medium text-gray-900 capitalize">{site?.settings?.target_audience}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Questions per Location</dt>
            <dd className="text-sm font-medium text-gray-900">{site?.settings?.questions_per_location}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Auto-generate Questions</dt>
            <dd className="text-sm font-medium text-gray-900">
              {site?.settings?.auto_generate_questions ? 'Enabled' : 'Disabled'}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Auto-publish</dt>
            <dd className="text-sm font-medium text-gray-900">
              {site?.settings?.publish_immediately ? 'Enabled' : 'Disabled'}
            </dd>
          </div>
        </dl>
      </div>

      {/* API Access */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Key className="h-5 w-5 text-purple-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">API Access</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500">API URL</label>
            <div className="flex items-center space-x-2 mt-1">
              <code className="flex-1 px-3 py-2 bg-gray-100 rounded-md text-sm font-mono text-gray-900 overflow-x-auto">
                {apiUrl}
              </code>
              <button
                onClick={() => copyToClipboard(apiUrl, 'url')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {copied === 'url' ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-500">API Key</label>
            <div className="flex items-center space-x-2 mt-1">
              <code className="flex-1 px-3 py-2 bg-gray-100 rounded-md text-sm font-mono text-gray-900">
                {apiKey?.slice(0, 8)}{'•'.repeat(20)}
              </code>
              <button
                onClick={() => apiKey && copyToClipboard(apiKey, 'key')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {copied === 'key' ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Integration Code */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Code className="h-5 w-5 text-orange-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Integration Code</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Use this code to fetch and display answer content on your website:
        </p>
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm text-gray-100">
{`// Fetch answer content
const response = await fetch('${apiUrl}/v1/render/{answerId}?format=json', {
  headers: {
    'X-API-Key': '${apiKey?.slice(0, 8)}...'
  }
});
const { data } = await response.json();

// data.content - Full structured content
// data.schemas - Schema markup for SEO
// data.meta - Meta tags for the page`}
          </pre>
        </div>
        <button
          onClick={() => copyToClipboard(`const response = await fetch('${apiUrl}/v1/render/{answerId}?format=json', {\n  headers: {\n    'X-API-Key': '${apiKey}'\n  }\n});\nconst { data } = await response.json();`, 'code')}
          className="mt-3 btn-secondary text-sm"
        >
          {copied === 'code' ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-2" />
              Copy Code
            </>
          )}
        </button>
      </div>

      {/* Available Templates */}
      {templates && templates.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Industry Templates</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template: any) => (
              <div
                key={template.id}
                className={`p-4 rounded-lg border-2 ${
                  template.slug === site?.business_type
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <h3 className="font-medium text-gray-900">{template.name}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {template.categories?.length} categories
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Tone: {template.content_tone} | CTA: {template.cta_type}
                </p>
                {template.slug === site?.business_type && (
                  <span className="inline-flex items-center mt-2 text-xs text-blue-600 font-medium">
                    <Check className="h-3 w-3 mr-1" />
                    Active
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
